import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { CiscoGeneralSettings, LicenseInfo, OwnerLineAssociation } from '../../../shared/models/generated/ziroModels';
import {
  ZiroFieldComponentConfig,
  ZiroFieldConfig,
  ZiroFormConfig,
  ZiroFormsValidationState,
} from '../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import {
  HtmlCheckboxType,
  HtmlSwitchSize,
  ZiroCheckboxConfig,
} from '../../../forms/fields/checkbox/ziro-checkbox.component';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { combineLatest, Observable, Subscriber, Subscription } from 'rxjs';
import { LicenseContext } from '../../../shared/contexts/license.context';
import { TranslateService } from '@ngx-translate/core';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { get } from 'lodash';
import { CiscoGeneralSettingsResource } from '../../resources/cisco-general-settings.resource';
import { HelpdeskOptionsContext } from '../../../helpdesk/shared/contexts/helpdesk-options.context';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../forms/fields/select/ziro-select.component';

export interface CiscoGeneralAndSelfServeSettingsForm {
  //General Settings
  associateOwnerToLine: ZiroSelectOption;
  automaticPhoneTemplateSelectionEnabled: boolean;
  changeSiteEnabled: boolean;
  deprovisionThreshold: number;
  display360ViewClusterDropdownEnabled: boolean;
  displayEnhancedLineMode: boolean;
  enableWebexPerSite: boolean;
  maxOwnedDevices: number;
  minimumOverageDaysSmartLicensing: number;
  resetPinOnNextLoginEnabled: boolean;
  showAddUserPage: boolean;
  tasksDisabled: boolean;
  unifiedFxUrl: string;
  unityLookupWithAndWithoutNaCountryCode: boolean;
  zpmUrl: string;
  //Self-Serve Settings
  selfServeCallForwardEditEnabled: boolean;
  selfServeDeskphoneProvisioningEnabled: boolean;
  selfServeSnrEditEnabled: boolean;
  selfServeSpeedDialEditEnabled: boolean;
  selfServeVoicemailPinResetComplexity: string;
}

@Component({
  selector: 'ziro-cisco-general-settings',
  templateUrl: './cisco-general-settings.component.html',
  styleUrls: ['../../admin-page.scss'],
  standalone: false,
})
export class CiscoGeneralSettingsComponent
  extends ZiroFormAbstractDirective<CiscoGeneralSettings, CiscoGeneralAndSelfServeSettingsForm>
  implements OnInit, OnDestroy
{
  private toastService = inject(ToastService);
  private ciscoGeneralSettingsResource = inject(CiscoGeneralSettingsResource);
  private bottomNavService = inject(BottomNavService);
  protected _ziroFormStateService: ZiroFormStateService;
  private breadcrumbsService = inject(BreadcrumbsService);
  private licenseContext = inject(LicenseContext);
  private translateService = inject(TranslateService);
  private helpdeskOptionsContext = inject(HelpdeskOptionsContext);

  isLoading = true;
  ziroIcons = ZiroIcons;
  bottomNavButtons = [
    {
      id: 'saveCiscoGeneralSettings',
      dataAutomation: 'save-cisco-general-settings-button',
      label: 'tkey;admin.ui.save',
      buttonClass: ButtonStyles.PRIMARY,
      state: {
        pending: false,
        buttonDisableState: {
          disabled: false,
          tooltipKey: '',
        },
      },
      icon: this.ziroIcons.SAVE_SUCCESS,
      type: ButtonTypes.SUBMIT,
      submitSubject: this._validateAndSubmitSource,
    },
  ] as BottomNavButton[];

  possibleAssociateOwnerToLineOptions: ZiroSelectOption[] = [
    { label: 'All Lines', value: OwnerLineAssociation.ALL_LINES },
    { label: 'Primary Extension Only', value: OwnerLineAssociation.PRIMARY_LINE_ONLY },
    { label: 'No Lines', value: OwnerLineAssociation.NO_LINES },
  ];

  generalFields = [
    'resetPinOnNextLoginEnabled',
    'displayEnhancedLineMode',
    'showAddUserPage',
    'automaticPhoneTemplateSelectionEnabled',
    'changeSiteEnabled',
    'display360ViewClusterDropdownEnabled',
    'enableWebexPerSite',
    'associateOwnerToLine',
    'minimumOverageDaysSmartLicensing',
    'unifiedFxUrl',
    'zpmUrl',
    'maxOwnedDevices',
  ];

  selfServeFields = [
    'selfServeDeskphoneProvisioningEnabled',
    'selfServeCallForwardEditEnabled',
    'selfServeSnrEditEnabled',
    'selfServeSpeedDialEditEnabled',
    'selfServeVoicemailPinResetComplexity',
  ];

  formConfig = {
    fields: {
      resetPinOnNextLoginEnabled: {
        dataAutomation: 'reset-pin-on-next-login-enabled',
        label: 'tkey;admin.system.cisco_general_settings.voicemail_and_ext_mobility.reset_pin_on_next_login.label',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content:
            'tkey;admin.system.cisco_general_settings.voicemail_and_ext_mobility.reset_pin_on_next_login.content',
        },
      },
      selfServeDeskphoneProvisioningEnabled: {
        dataAutomation: 'self-serve-deskphone-provisioning-enabled',
        label: 'tkey;admin.system.cisco_general_settings.selfserve.enable.deskphone.provisioning.edit',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        disabled: () => !get(this._licenseInfo, 'modules.automation'),
        disabledTooltip: 'tkey;admin.system.cisco_general_settings.selfserve.enable.deskphone.provisioning.disabled',
        options: {
          content: 'tkey;admin.system.cisco_general_settings.selfserve.enable.deskphone.provisioning.edit.content',
        },
      },
      displayEnhancedLineMode: {
        dataAutomation: 'display-enhanced-line-mode',
        label: 'tkey;admin.system.cisco_general_settings.phone_button_display.label',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.phone_button_display.content',
        },
      },
      showAddUserPage: {
        dataAutomation: 'show-add-user-page',
        label: 'tkey;admin.system.cisco_general_settings.show.add.user.page.label',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.show.add.user.page.content',
        },
      },
      selfServeCallForwardEditEnabled: {
        dataAutomation: 'self-serve-call-forward-edit-enabled',
        label: 'tkey;admin.system.cisco_general_settings.selfserve.enable.call.forward.edit',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.selfserve.enable.call.forward.edit.content',
        },
      },
      selfServeSnrEditEnabled: {
        dataAutomation: 'self-serve-snr-edit-enabled',
        label: 'tkey;admin.system.cisco_general_settings.selfserve.enable.snr.edit',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.selfserve.enable.snr.edit.content',
        },
      },
      selfServeSpeedDialEditEnabled: {
        dataAutomation: 'self-serve-speed-dial-edit-enabled',
        label: 'tkey;admin.system.cisco_general_settings.selfserve.enable.speed_dial.edit',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.selfserve.enable.speed_dial.edit.content',
        },
      },
      automaticPhoneTemplateSelectionEnabled: {
        dataAutomation: 'automatic-phone-template-selection-enabled',
        label: 'tkey;admin.system.cisco_general_settings.automatic_phone_button_template_selection.label',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.automatic_phone_button_template_selection.content',
        },
      },
      changeSiteEnabled: {
        dataAutomation: 'change-site-enabled',
        label: 'tkey;admin.system.cisco_general_settings.change_site_device_pool_only.label',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.change_site_device_pool_only.content',
        },
      },
      display360ViewClusterDropdownEnabled: {
        dataAutomation: 'display-360-view-cluster-dropdown-enabled',
        label: 'tkey;admin.system.cisco_general_settings.display_360_view_cluster_dropdown.label',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.display_360_view_cluster_dropdown.content',
        },
      },
      enableWebexPerSite: {
        dataAutomation: 'enable-webex-per-site',
        label: 'tkey;admin.system.cisco_general_settings.enable_webex_per_site.label',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.enable_webex_per_site.content',
        },
      },
      associateOwnerToLine: {
        dataAutomation: 'associate-owner-to-line',
        label: 'tkey;admin.system.cisco_general_settings.associate_owner_to_lines.label',
        componentConfig: new ZiroSelectConfig({ options: this.possibleAssociateOwnerToLineOptions }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.associate_owner_to_lines.content',
        },
      },
      minimumOverageDaysSmartLicensing: {
        dataAutomation: 'minimum-overage-days-smart-licensing',
        label: 'tkey;admin.system.cisco_general_settings.minimum_overage_days_smart_licensing.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
        required: () => true,
        validation: [
          {
            validator: (val: number) => {
              return val >= 0 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
            },
            message:
              'tkey;admin.system.cisco_general_settings.minimum_overage_days_smart_licensing.validation.positive.error',
          },
        ],
        options: {
          content: 'tkey;admin.system.cisco_general_settings.minimum_overage_days_smart_licensing.content',
        },
      },
      unifiedFxUrl: {
        dataAutomation: 'unified-fx-url',
        label: 'tkey;admin.system.cisco_general_settings.unified_fx_url.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.unified_fx_url.content',
        },
      },
      zpmUrl: {
        dataAutomation: 'zpm-url',
        label: 'tkey;admin.system.cisco_general_settings.zpm_url.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.zpm_url.content',
        },
      },
      maxOwnedDevices: {
        dataAutomation: 'max-owned-devices',
        label: 'tkey;admin.system.cisco_general_settings.max_owned_devices.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
        required: () => true,
        validation: [
          {
            validator: (val: number) => (val >= 1 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID),
            message: this.translateService.instant('tkey;admin.audits.logs.audit_days_to_keep.positive.error.text'),
          },
        ],
        options: {
          content: 'tkey;admin.system.cisco_general_settings.max_owned_devices.content',
        },
      },
      selfServeVoicemailPinResetComplexity: {
        dataAutomation: 'self-serve-voicemail-pin-reset-complexity',
        label: 'tkey;admin.system.cisco_general_settings.selfserve.voicemail_pin_reset_complexity.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        options: {
          content: 'tkey;admin.system.cisco_general_settings.selfserve.voicemail_pin_reset_complexity.content',
        },
      },
    },
  } as ZiroFormConfig;

  private _config = {} as CiscoGeneralSettings;
  private _licenseInfo = {} as LicenseInfo;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.system.cisco_general_settings.title' }]);
    this.bottomNavService.dispatch(new BottomNavUpdateButtonsList(this.bottomNavButtons));

    combineLatest([this.ciscoGeneralSettingsResource.get(), this.licenseContext.licenseState$]).subscribe((data) => {
      this._config = data[0];
      this._licenseInfo = data[1];

      this.entitySource.next({
        ...this._config,
        selfServeDeskphoneProvisioningEnabled:
          this._config.selfServeDeskphoneProvisioningEnabled && this._licenseInfo.modules.automation,
      });

      this.isLoading = false;
    });

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() =>
      this._setBottomNavValidationError(!this.isFormValid()),
    );
    this._subscriptions.add(formSubmittedSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
    this.breadcrumbsService.clearBreadcrumbs();
  }

  isCheckBox(config: ZiroFieldConfig<ZiroFieldComponentConfig>): boolean {
    return config.componentConfig instanceof ZiroCheckboxConfig;
  }

  isTextInput(config: ZiroFieldConfig<ZiroFieldComponentConfig>): boolean {
    return config.componentConfig instanceof ZiroTextConfig;
  }

  isSelect(config: ZiroFieldConfig<ZiroFieldComponentConfig>): boolean {
    return config.componentConfig instanceof ZiroSelectConfig;
  }

  // Used to keep the order of the keys-value pairs as we iterate formConfig.fields using 'keyvalue' pipe
  returnZero: () => number = () => 0;

  toEntity = (formData: CiscoGeneralAndSelfServeSettingsForm): CiscoGeneralSettings => {
    return {
      ...formData,
      associateOwnerToLine: this._getEnumValueFromString(formData.associateOwnerToLine?.value),
    };
  };

  toFormData = (entity: CiscoGeneralSettings): CiscoGeneralAndSelfServeSettingsForm => {
    return {
      ...entity,
      associateOwnerToLine: this.possibleAssociateOwnerToLineOptions.find(
        (option) => option.value === entity.associateOwnerToLine,
      ),
    } as CiscoGeneralAndSelfServeSettingsForm;
  };

  protected submit(): Observable<void> {
    return this._saveGeneralSettings();
  }

  private _getEnumValueFromString(value: string): OwnerLineAssociation | undefined {
    return OwnerLineAssociation[value as keyof typeof OwnerLineAssociation];
  }

  private _saveGeneralSettings(): Observable<void> {
    return new Observable((subscriber: Subscriber<void>) => {
      this._setBottomNavValidationError(false);
      this._setBottomNavPending(true);

      this.ciscoGeneralSettingsResource.put(this.entity).subscribe(() => {
        this._setBottomNavPending(false);

        this.toastService.push(
          ToastTypes.SUCCESS,
          this.ziroIcons.SAVE_SUCCESS,
          'tkey;shared.toast.save.success.title',
          'tkey;admin.system.cisco_general_settings.save.toast',
        );

        this.helpdeskOptionsContext.refresh().subscribe(() => {
          subscriber.next(null);
          subscriber.complete();
        });
      });
    });
  }

  private _setBottomNavValidationError(hasError: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: hasError,
      }),
    );
  }

  private _setBottomNavPending(setting: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'saveCiscoGeneralSettings',
        state: {
          pending: setting,
        },
      }),
    );
  }
}
